import { Fragment, useState } from "react";
import { Menu, X } from "lucide-react";
import { SITE_BRAND_DISPLAY_NAME } from "@/config/siteBrand";
import { SERVICE_SUBMENU_ITEMS } from "@/config/serviceSubNavRoutes";
import { LangFlagRo, LangFlagUk, LangFlagUa, LangFlagChip } from "@/components/LangFlag";

const navItems = [
  { key: "services", section: "services" },
  { key: "whyUs", section: "why" },
  { key: "rcaPartners", section: "parteneri-rca" },
  { key: "brands", section: "brands" },
  { key: "booking", section: "booking" },
  { key: "contact", section: "contact" },
  { key: "vinParts", path: "/piese-dupa-vin" },
];

export default function SiteHeader({ t, lang, setLang }) {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  const goToSection = (id) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goToPath = (path) => {
    setOpen(false);
    if (window.location.pathname === path) return;
    window.location.href = path;
  };

  const serviceSubmenu = SERVICE_SUBMENU_ITEMS.map(({ navKey, path }) => ({
    key: navKey,
    path: navKey === "claims" ? "http://localhost:3000/constatari-daune" : path,
  }));

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-[#e2e6ec]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 lg:h-24 flex items-center justify-between">
        {/* Logo */}
        <button
          data-testid="logo-home"
          onClick={() => {
            if (window.location.pathname !== "/") {
              window.location.href = "/";
              return;
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <img
            src="/resources/logo-selena.png"
            alt={SITE_BRAND_DISPLAY_NAME}
            className="h-12 lg:h-14 w-auto"
          />
        </button>

        <div className="flex items-center gap-3">
          {/* Language toggle */}
          <div
            data-testid="lang-toggle"
            className="flex items-center border border-[#e2e6ec] font-mono text-xs"
          >
            <button
              type="button"
              data-testid="lang-ro"
              onClick={() => setLang("ro")}
              aria-label="Română"
              title="Română"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 transition-colors ${
                lang === "ro"
                  ? "bg-[#0b378c] text-white font-bold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]"
                  : "text-slate-600 hover:text-[#0b378c]"
              }`}
            >
              <LangFlagChip>
                <LangFlagRo />
              </LangFlagChip>
              RO
            </button>
            <button
              type="button"
              data-testid="lang-en"
              onClick={() => setLang("en")}
              aria-label="English"
              title="English"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 transition-colors border-l border-[#e2e6ec] ${
                lang === "en"
                  ? "bg-[#0b378c] text-white font-bold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]"
                  : "text-slate-600 hover:text-[#0b378c]"
              }`}
            >
              <LangFlagChip>
                <LangFlagUk />
              </LangFlagChip>
              EN
            </button>
            <button
              type="button"
              data-testid="lang-uk"
              onClick={() => setLang("uk")}
              aria-label="Українська"
              title="Українська"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 transition-colors border-l border-[#e2e6ec] ${
                lang === "uk"
                  ? "bg-[#0b378c] text-white font-bold shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]"
                  : "text-slate-600 hover:text-[#0b378c]"
              }`}
            >
              <LangFlagChip>
                <LangFlagUa />
              </LangFlagChip>
              UA
            </button>
          </div>

          <button
            data-testid="header-book-cta"
            onClick={() => goToSection("booking")}
            className="hidden sm:inline-flex bg-[#e3191b] hover:bg-[#c01416] text-white font-bold uppercase tracking-wider text-xs px-5 py-3 transition-colors"
          >
            {t.nav.bookNow}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[#0b1220] p-2"
            aria-label="menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <div className="hidden lg:block" onMouseLeave={() => setServicesOpen(false)}>
        <div className="border-t border-[#e2e6ec]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-12 flex items-center justify-center gap-8">
            {navItems.map((item) => (
              <Fragment key={item.key}>
                {item.key === "vinParts" ? (
                  <span
                    className="hidden lg:inline-block w-px h-5 shrink-0 self-center bg-[#cfd6e0]"
                    aria-hidden="true"
                  />
                ) : null}
                <button
                  data-testid={`nav-${item.key}`}
                  onClick={() => (item.path ? goToPath(item.path) : goToSection(item.section))}
                  onMouseEnter={() => {
                    if (item.key === "services") setServicesOpen(true);
                    else setServicesOpen(false);
                  }}
                  onFocus={() => {
                    if (item.key === "services") setServicesOpen(true);
                  }}
                  className="text-xs font-mono uppercase tracking-[0.2em] text-slate-700 hover:text-[#0b378c] transition-colors"
                >
                  {t.nav[item.key]}
                </button>
              </Fragment>
            ))}
          </div>
        </div>
        <div
          className={`border-t border-[#e2e6ec] bg-[#f7f8fa] ${servicesOpen ? "" : "invisible h-0 overflow-hidden border-t-0 py-0"}`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-center gap-2 flex-wrap">
            {serviceSubmenu.map((sub) => (
              <button
                key={sub.key}
                data-testid={`nav-sub-${sub.key}`}
                onClick={() => goToPath(sub.path)}
                className="px-3 py-1.5 border border-transparent text-xs font-mono uppercase tracking-[0.18em] text-slate-600 hover:text-[#0b378c] hover:border-[#d7deea] hover:bg-white transition-colors"
              >
                {t.nav[sub.key]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#e2e6ec] bg-white"
        >
          <div className="px-4 py-4 flex flex-col gap-1">
            {navItems.map((item) => (
              <div key={item.key}>
                {item.key === "vinParts" ? (
                  <div
                    className="border-t border-[#d7deea] mt-3 pt-3"
                    role="separator"
                    aria-hidden="true"
                  />
                ) : null}
                <button
                  data-testid={`mobile-nav-${item.key}`}
                  onClick={() => (item.path ? goToPath(item.path) : goToSection(item.section))}
                  className="w-full text-left py-3 px-2 text-sm font-mono uppercase tracking-[0.2em] text-slate-700 hover:text-[#0b378c] border-b border-[#eef0f4]"
                >
                  {t.nav[item.key]}
                </button>
                {item.key === "services" && (
                  <div className="border-b border-[#eef0f4] pb-2">
                    {serviceSubmenu.map((sub) => (
                      <button
                        key={sub.key}
                        data-testid={`mobile-nav-sub-${sub.key}`}
                        onClick={() => goToPath(sub.path)}
                        className="w-full text-left py-2 pl-6 pr-2 text-xs font-mono uppercase tracking-[0.2em] text-slate-600 hover:text-[#0b378c]"
                      >
                        {t.nav[sub.key]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
